import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { X, Send, Users, Stethoscope, Search, Clock, Check } from "@/components/icons";
import Portal from "./Portal";
import AppleModal from "./AppleModal";
import { doctorLabel } from "../lib/doctorLabel";

/**
 * Modal para crear un referido médico. Prellena paciente cuando se abre
 * desde el expediente. Soporta dos modos exclusivos:
 *   - INTERNO   → picker de doctor destino (dentro de la plataforma)
 *   - EXTERNO   → especialidad libre + nombre doctor + centro
 *
 * Props:
 *  - patientId (string, optional)
 *  - patientName (string, optional)
 *  - onClose (fn)
 *  - onSaved (fn)
 */
export default function ReferralModal({
  patientId = "",
  patientName = "",
  onClose,
  onSaved,
}) {
  const [mode, setMode] = useState("internal"); // "internal" | "external"
  const [patients, setPatients] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Búsqueda / autocompletado de paciente
  const [patientQuery, setPatientQuery] = useState(patientName);
  const [showSuggest, setShowSuggest] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const suggestRef = useRef(null);
  // Búsqueda / autocompletado de doctor destino
  const [doctorQuery, setDoctorQuery] = useState("");
  const [showDocSuggest, setShowDocSuggest] = useState(false);
  const [docHighlight, setDocHighlight] = useState(-1);
  const docSuggestRef = useRef(null);
  const [form, setForm] = useState({
    patient_id: patientId,
    to_doctor_id: "",
    external_specialty: "",
    external_doctor_name: "",
    external_center: "",
    external_email: "",
    external_whatsapp: "",
    reason: "",
    clinical_notes: "",
    urgency: "rutina",
  });

  useEffect(() => {
    if (!patientId) {
      api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
      api
        .get("/patients/recientes?limit=2")
        .then((r) => setRecentPatients(r.data))
        .catch(() => {});
    }
    api.get("/users/doctors").then((r) => setDoctors(r.data)).catch(() => {});
  }, [patientId]);

  // Cerrar sugerencias al click fuera
  useEffect(() => {
    const onDoc = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setShowSuggest(false);
      }
      if (docSuggestRef.current && !docSuggestRef.current.contains(e.target)) {
        setShowDocSuggest(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filteredPatients = useMemo(() => {
    const q = patientQuery.trim().toLowerCase();
    if (!q) return [];
    return patients
      .filter(
        (p) =>
          p.full_name?.toLowerCase().includes(q) ||
          (p.document_id || "").toLowerCase().includes(q) ||
          (p.phone || "").includes(q)
      )
      .slice(0, 8);
  }, [patientQuery, patients]);

  const filteredDoctors = useMemo(() => {
    const q = doctorQuery.trim().toLowerCase();
    // Mostrar todos cuando abre sin escribir (top 10)
    if (!q) return doctors.slice(0, 10);
    return doctors
      .filter(
        (d) =>
          d.name?.toLowerCase().includes(q) ||
          (d.specialty || "").toLowerCase().includes(q) ||
          (d.email || "").toLowerCase().includes(q)
      )
      .slice(0, 10);
  }, [doctorQuery, doctors]);

  const pickPatient = (p) => {
    set("patient_id", p.id);
    setPatientQuery(p.full_name);
    setShowSuggest(false);
    setHighlight(-1);
  };

  const pickDoctor = (d) => {
    set("to_doctor_id", d.id);
    setDoctorQuery(
      `${doctorLabel(d.name)}${d.specialty ? ` · ${d.specialty}` : ""}`
    );
    setShowDocSuggest(false);
    setDocHighlight(-1);
  };

  const onKeyDownSearch = (e) => {
    if (!showSuggest || filteredPatients.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filteredPatients.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pickPatient(filteredPatients[highlight]);
    } else if (e.key === "Escape") {
      setShowSuggest(false);
    }
  };

  const onKeyDownDoctorSearch = (e) => {
    if (!showDocSuggest || filteredDoctors.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setDocHighlight((h) => Math.min(h + 1, filteredDoctors.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setDocHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && docHighlight >= 0) {
      e.preventDefault();
      pickDoctor(filteredDoctors[docHighlight]);
    } else if (e.key === "Escape") {
      setShowDocSuggest(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) {
      toast.error("Selecciona un paciente");
      return;
    }
    if (mode === "internal" && !form.to_doctor_id) {
      toast.error("Selecciona un doctor destino");
      return;
    }
    if (mode === "external" && !form.external_specialty.trim()) {
      toast.error("Escribe la especialidad externa");
      return;
    }
    if (form.reason.trim().length < 3) {
      toast.error("Escribe un motivo (mínimo 3 caracteres)");
      return;
    }
    const payload = {
      patient_id: form.patient_id,
      reason: form.reason.trim(),
      clinical_notes: form.clinical_notes.trim(),
      urgency: form.urgency,
    };
    if (mode === "internal") {
      payload.to_doctor_id = form.to_doctor_id;
    } else {
      payload.external_specialty = form.external_specialty.trim();
      payload.external_doctor_name = form.external_doctor_name.trim() || null;
      payload.external_center = form.external_center.trim() || null;
      payload.external_email = form.external_email.trim() || null;
      payload.external_whatsapp = form.external_whatsapp.trim() || null;
    }
    setSubmitting(true);
    try {
      await api.post("/referrals", payload);
      toast.success("Referido creado");
      onSaved?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppleModal
      open
      onClose={submitting ? undefined : onClose}
      icon={<Send weight="regular" className="w-8 h-8" strokeWidth={1.5} />}
      title="Nuevo referido"
      subtitle="Envía un paciente a otro especialista"
      size="lg"
      compact
      testid="referral-modal"
    >
      <form onSubmit={submit} className="space-y-4 max-h-[62vh] overflow-y-auto no-scrollbar pr-1">
            {/* Toggle interno / externo */}
            <div className="grid grid-cols-2 gap-1 p-1 bg-[#F2F3F5] rounded-lg">
              <button
                type="button"
                onClick={() => setMode("internal")}
                data-testid="mode-internal"
                className={`h-9 rounded-md text-[13px] font-semibold transition inline-flex items-center justify-center gap-1.5 ${
                  mode === "internal"
                    ? "bg-white text-black shadow-sm"
                    : "text-black/50 hover:text-black/70"
                }`}
              >
                <Users className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" /> Doctor en plataforma
              </button>
              <button
                type="button"
                onClick={() => setMode("external")}
                data-testid="mode-external"
                className={`h-9 rounded-md text-[13px] font-semibold transition inline-flex items-center justify-center gap-1.5 ${
                  mode === "external"
                    ? "bg-white text-black shadow-sm"
                    : "text-black/50 hover:text-black/70"
                }`}
              >
                <Stethoscope className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" /> Especialista externo
              </button>
            </div>

            {/* Paciente */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                Paciente
              </label>
              {patientId ? (
                <div className="h-10 px-3 rounded-lg bg-[#F2F3F5] border border-black/[0.05] text-[13px] font-semibold text-black flex items-center">
                  {patientName || "Paciente actual"}
                </div>
              ) : (
                <div ref={suggestRef} className="space-y-2">
                  {/* Chips: últimos 2 pacientes con consulta */}
                  {recentPatients.length > 0 && !form.patient_id && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-black/40 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Recientes
                      </span>
                      {recentPatients.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`recent-patient-${p.id}`}
                          onClick={() => pickPatient(p)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#F2F3F5] hover:bg-black/[0.08] text-black/70 text-[12px] font-semibold transition"
                        >
                          {p.full_name}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Input búsqueda con sugerencias */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 pointer-events-none" />
                    <input
                      data-testid="ref-patient-search"
                      value={patientQuery}
                      onChange={(e) => {
                        setPatientQuery(e.target.value);
                        setShowSuggest(true);
                        setHighlight(-1);
                        if (form.patient_id) set("patient_id", "");
                      }}
                      onFocus={() => setShowSuggest(true)}
                      onKeyDown={onKeyDownSearch}
                      placeholder="Buscar por nombre, cédula o teléfono…"
                      autoComplete="off"
                      className="w-full h-10 pl-9 pr-9 rounded-lg border border-black/[0.08] bg-white text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    />
                    {form.patient_id && (
                      <Check
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black"
                        data-testid="ref-patient-selected"
                        strokeWidth={1.75}
                        weight="regular"
                      />
                    )}
                    {showSuggest && filteredPatients.length > 0 && (
                      <div
                        className="absolute z-10 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-black/[0.08] shadow-lg max-h-64 overflow-y-auto"
                        data-testid="ref-patient-suggest"
                      >
                        {filteredPatients.map((p, i) => (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`suggest-patient-${p.id}`}
                            onClick={() => pickPatient(p)}
                            onMouseEnter={() => setHighlight(i)}
                            className={`w-full text-left px-3 py-2 text-[13px] border-b border-black/[0.05] last:border-0 transition ${
                              highlight === i
                                ? "bg-black/[0.04] text-black"
                                : "hover:bg-black/[0.02] text-black/70"
                            }`}
                          >
                            <div className="font-semibold text-black">{p.full_name}</div>
                            {(p.document_id || p.phone) && (
                              <div className="text-[11px] text-black/45 mt-0.5">
                                {p.document_id ? `${p.document_id}` : ""}
                                {p.document_id && p.phone ? " · " : ""}
                                {p.phone || ""}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {showSuggest &&
                      patientQuery.trim() &&
                      filteredPatients.length === 0 && (
                        <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-black/[0.08] shadow-lg px-3 py-2.5 text-[12px] text-black/45">
                          Sin resultados para “{patientQuery}”
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>

            {/* Destino */}
            {mode === "internal" ? (
              <div ref={docSuggestRef}>
                <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                  Doctor destino
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 pointer-events-none" />
                  <input
                    data-testid="ref-doctor-search"
                    value={doctorQuery}
                    onChange={(e) => {
                      setDoctorQuery(e.target.value);
                      setShowDocSuggest(true);
                      setDocHighlight(-1);
                      if (form.to_doctor_id) set("to_doctor_id", "");
                    }}
                    onFocus={() => setShowDocSuggest(true)}
                    onKeyDown={onKeyDownDoctorSearch}
                    placeholder="Buscar por nombre, especialidad o email…"
                    autoComplete="off"
                    className="w-full h-10 pl-9 pr-9 rounded-lg border border-black/[0.08] bg-white text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                  />
                  {form.to_doctor_id && (
                    <Check
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black"
                      data-testid="ref-doctor-selected"
                      strokeWidth={1.75}
                      weight="regular"
                    />
                  )}
                  {showDocSuggest && filteredDoctors.length > 0 && (
                    <div
                      className="absolute z-10 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-black/[0.08] shadow-lg max-h-64 overflow-y-auto"
                      data-testid="ref-doctor-suggest"
                    >
                      {filteredDoctors.map((d, i) => (
                        <button
                          key={d.id}
                          type="button"
                          data-testid={`suggest-doctor-${d.id}`}
                          onClick={() => pickDoctor(d)}
                          onMouseEnter={() => setDocHighlight(i)}
                          className={`w-full text-left px-3 py-2 text-[13px] border-b border-black/[0.05] last:border-0 transition ${
                            docHighlight === i
                              ? "bg-black/[0.04] text-black"
                              : "hover:bg-black/[0.02] text-black/70"
                          }`}
                        >
                          <div className="font-semibold text-black">
                            {doctorLabel(d.name)}
                          </div>
                          <div className="text-[11px] text-black/45 mt-0.5">
                            {d.specialty || "Medicina General"}
                            {d.email ? ` · ${d.email}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showDocSuggest &&
                    doctorQuery.trim() &&
                    filteredDoctors.length === 0 && (
                      <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-black/[0.08] shadow-lg px-3 py-2.5 text-[12px] text-black/45">
                        Sin resultados para “{doctorQuery}”
                      </div>
                    )}
                </div>
                {doctors.length === 0 && (
                  <p className="text-[11px] text-amber-700 mt-1.5">
                    No hay otros doctores en la plataforma. Usa &quot;Especialista externo&quot;.
                  </p>
                )}
              </div>
            ) : (
              <div className="grid gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                    Especialidad *
                  </label>
                  <input
                    data-testid="ref-external-specialty"
                    value={form.external_specialty}
                    onChange={(e) => set("external_specialty", e.target.value)}
                    placeholder="Ej: Cardiología, Neurología, Endocrinología…"
                    className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                      Nombre del doctor (opcional)
                    </label>
                    <input
                      data-testid="ref-external-name"
                      value={form.external_doctor_name}
                      onChange={(e) => set("external_doctor_name", e.target.value)}
                      placeholder="Dr. Juan Pérez"
                      className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                      Centro / Clínica (opcional)
                    </label>
                    <input
                      data-testid="ref-external-center"
                      value={form.external_center}
                      onChange={(e) => set("external_center", e.target.value)}
                      placeholder="Hospital / Clínica"
                      className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                      Email del especialista (opcional)
                    </label>
                    <input
                      type="email"
                      data-testid="ref-external-email"
                      value={form.external_email}
                      onChange={(e) => set("external_email", e.target.value)}
                      placeholder="doctor@ejemplo.com"
                      className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                      WhatsApp (opcional)
                    </label>
                    <input
                      data-testid="ref-external-whatsapp"
                      value={form.external_whatsapp}
                      onChange={(e) => set("external_whatsapp", e.target.value)}
                      placeholder="+1 809 555 1234"
                      className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-black/60 bg-[#F2F3F5] border border-black/[0.05] rounded-lg px-3 py-2 flex gap-2">
                  <span className="text-black/70">✦</span>
                  <span>
                    Al crear el referido se generará un <b className="text-black">enlace público</b> (válido 90 días) con QR imprimible para que el especialista externo lo consulte y confirme recepción.
                  </span>
                </div>
              </div>
            )}

            {/* Motivo */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                Motivo / Diagnóstico presuntivo *
              </label>
              <input
                data-testid="ref-reason"
                value={form.reason}
                onChange={(e) => set("reason", e.target.value)}
                placeholder="Ej: HTA resistente para evaluación cardiológica"
                className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
              />
            </div>

            {/* Notas clínicas */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                Notas clínicas
              </label>
              <textarea
                data-testid="ref-notes"
                value={form.clinical_notes}
                onChange={(e) => set("clinical_notes", e.target.value)}
                rows={4}
                placeholder="Historia relevante, medicación actual, estudios previos…"
                className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-black resize-none focus:outline-none focus:border-black/40 transition"
              />
            </div>

            {/* Urgencia */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                Urgencia
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "rutina", label: "Rutina" },
                  { v: "urgente", label: "Urgente" },
                ].map((u) => (
                  <button
                    key={u.v}
                    type="button"
                    data-testid={`ref-urgency-${u.v}`}
                    onClick={() => set("urgency", u.v)}
                    className={`h-10 rounded-lg text-[13px] font-semibold transition ${
                      form.urgency === u.v
                        ? u.v === "urgente"
                          ? "bg-red-500 text-white"
                          : "bg-black text-white"
                        : "bg-[#F2F3F5] text-black/60 hover:bg-black/[0.08]"
                    }`}
                  >
                    {u.label}
                  </button>
                ))}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-black/[0.05]">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="h-9 px-3.5 rounded-lg bg-white border border-black/10 hover:bg-black/[0.04] text-[12.5px] font-semibold text-black transition disabled:opacity-50"
              data-testid="ref-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="ref-submit"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[#0071E3] hover:bg-[#0077ED] text-white font-semibold text-[13px] transition disabled:opacity-50"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
              {submitting ? "Enviando…" : "Enviar referido"}
            </button>
          </div>
    </AppleModal>
  );
}
